import React from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { Box, Button, Grid, styled, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { CustomerSegment } from 'components/customers/models/Customer';
import { Perk } from 'components/loyalty/models/PerkModel';
import { deleteCampaignItem } from 'store/campaigns/campaignsAction';
import { CampaignsActionType } from 'store/campaigns/campaignsActionTypes';
import { ApplicationState } from 'store/store';
import { LoadingButton } from 'ui/buttons/LoadingButton';
import { Row } from 'ui/Flex';
import { CampaignItem } from '../models/CampaignItem';
import { CreateCampaignItem } from './CreateCampaignItem';

interface CreateCampaignSettingsProps {
    open: boolean;
    audiences: CustomerSegment[];
    perks: Perk[];
    isLoading?: boolean;
    onItemSubmit: (item: CampaignItem, index: number) => void;
    onSubmit: () => void;
}

const StyledBox = styled(Box)(({ theme }) => ({
    '&:before': {
        content: '""',
        position: 'absolute',
        width: '1px',
        backgroundColor: theme.palette.primary.light,
        height: `calc(100% - calc(${theme.spacing(6)} + 1px))`,
        top: theme.spacing(2) + 1,
        left: theme.spacing(4)
    }
}));

export const CreateCampaignSettings: React.FC<CreateCampaignSettingsProps> = props => {
    const { isLoading, open, onItemSubmit, audiences, perks, onSubmit } = props;

    const { campaignItems } = useSelector((state: ApplicationState) => state.campaigns);
    const dispatch = useDispatch();
    const store = useStore();

    const handleAddNew = React.useCallback(() => {
        dispatch({
            type: CampaignsActionType.ADD_EMPTY_CAMPAIGN
        });
    }, [dispatch]);
    const handleDelete = React.useCallback(
        (index: number) => {
            deleteCampaignItem(index)(dispatch, store.getState);
        },
        [dispatch, store.getState]
    );

    if (!open) {
        return null;
    }
    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Box padding={3}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <Typography variant="h4" color="secondary">
                                2. Settings
                            </Typography>
                            <Typography variant="body2">
                                Choose a condition and effect to create a trigger. This trigger will then send
                                a campaign to the selected audience once you push it to live. It doesn’t
                                matter in what order those triggers are created.
                            </Typography>
                        </Grid>
                        <StyledBox position="relative">
                            {campaignItems.map((item, index) => (
                                <CreateCampaignItem
                                    key={`campaign-item-${item.id || 'New'}`}
                                    perks={perks}
                                    index={index}
                                    audiences={audiences}
                                    onSubmit={onItemSubmit}
                                    item={item}
                                    deletable={campaignItems.length > 1}
                                    onDelete={handleDelete}
                                />
                            ))}
                            {!!campaignItems.length && !!campaignItems[campaignItems.length - 1].id && (
                                <Button
                                    sx={{ mt: 2 }}
                                    color="primary"
                                    variant="outlined"
                                    onClick={handleAddNew}
                                >
                                    <AddIcon />
                                </Button>
                            )}
                        </StyledBox>
                        <Grid item xs={12}>
                            <Box padding={3} paddingRight={0}>
                                <Row flex={1} align="flex-end" gutter>
                                    <LoadingButton
                                        loading={isLoading}
                                        disabled={isLoading}
                                        color="primary"
                                        variant="contained"
                                        onClick={onSubmit}
                                    >
                                        Continue
                                    </LoadingButton>
                                </Row>
                            </Box>
                        </Grid>
                    </Grid>
                </Box>
            </Grid>
        </Grid>
    );
};
